/**
 * MoMent — COMPARER LES PDF SsdwLab d'un même dossier (recalcul, 21/10/2026).
 *
 * Un recalcul remplace le PDF actif ; l'ancien reste à l'historique. Pour que le
 * chargement et la validation se fassent en connaissance de cause, on met les
 * lectures côte à côte, champ par champ, et on dit ce qui a changé (datation,
 * terme, marqueurs, clarté nucale, risques…). Module pur : rien n'est écrit.
 */

type Risque = { borne: '=' | '<' | '>'; n: number } | null | undefined;
type Datation = { methode: string; ddr?: string; confirmeeEcho?: boolean; mesure?: string; valeurMm?: number; termeJours?: number; dateEcho?: string; dateConception?: string } | null | undefined;

/** Ce que la comparaison lit d'une lecture SsdwLab (sous-ensemble de `LectureSsdw`). */
export type LectureComparable = {
  profil?: string | null; nom?: string | null; idNo?: string | null; ddn?: string | null; ageDpa?: number | null; dpa?: string | null;
  dateDepistage?: string | null; termeJours?: number | null; datation?: Datation;
  foetus?: { nombre: number; chorionicite?: string | null; rang?: number | null } | null;
  marqueurs?: { code: string; libelle: string; valeur: number; unite: string; mom: number }[];
  cn?: { mm: number; lccMm: number; mom: number } | null; correctionsLigne?: string | null;
  signes?: { libelle: string; trisomie: number; resultat: string }[];
  risqueT21?: Risque; libelleT21?: string | null; risqueAge?: Risque; risqueT1318?: Risque; libelleT1318?: string | null;
  tubeNeural?: string | null; dateEdition?: string | null;
};

export type LigneComparaison = { cle: string; libelle: string; valeurs: string[]; change: boolean };

const fr = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};
const nb = (v: number): string => String(v).replace('.', ',');
const terme = (j: number | null | undefined): string => (j === null || j === undefined ? '—' : `${Math.floor(j / 7)} SA + ${j % 7} j`);
export const risqueLisible = (r: Risque): string => (r ? `${r.borne === '<' ? '< ' : r.borne === '>' ? '> ' : ''}1/${r.n}` : '—');

export function datationLisible(d: Datation): string {
  if (!d) return '—';
  if (d.methode === 'DDR') return `DDR du ${fr(d.ddr)}${d.confirmeeEcho ? ', confirmée par échographie' : ''}`;
  if (d.methode === 'biometrie') return `biométrie ${d.mesure} ${nb(d.valeurMm ?? 0)} mm = ${terme(d.termeJours)} à l'écho du ${fr(d.dateEcho)}`;
  if (d.methode === 'echographie') return `échographie du ${fr(d.dateEcho)} : ${terme(d.termeJours)}`;
  if (d.methode === 'conception') return `date de conception ${fr(d.dateConception)}`;
  return d.methode;
}

/**
 * Les lectures, de la plus ancienne à l'actuelle, en lignes « champ → une valeur par PDF ».
 * Un marqueur présent dans un seul PDF a sa ligne (« — » ailleurs) ; `change` = les valeurs diffèrent.
 */
export function comparerLectures(lectures: LectureComparable[]): LigneComparaison[] {
  const lignes: LigneComparaison[] = [];
  const ajouter = (cle: string, libelle: string, f: (l: LectureComparable) => string) => {
    const valeurs = lectures.map(f);
    lignes.push({ cle, libelle, valeurs, change: new Set(valeurs).size > 1 });
  };
  ajouter('profil', 'Profil', (l) => l.profil || '—');
  ajouter('patiente', 'Patiente', (l) => `${l.nom || '—'} · ID.No. ${l.idNo || '—'} · née le ${fr(l.ddn)}`);
  ajouter('foetus', 'Grossesse', (l) => (l.foetus && l.foetus.nombre > 1 ? `gémellaire${l.foetus.chorionicite ? ` (${l.foetus.chorionicite})` : ''}${l.foetus.rang ? `, fœtus ${l.foetus.rang}` : ''}` : 'monofœtale'));
  ajouter('datation', 'Datation', (l) => datationLisible(l.datation));
  ajouter('terme', 'Terme au dépistage', (l) => `${terme(l.termeJours)} le ${fr(l.dateDepistage)}`);
  ajouter('dpa', 'DPA', (l) => `${fr(l.dpa)}${l.ageDpa !== null && l.ageDpa !== undefined ? ` (${l.ageDpa} ans)` : ''}`);
  const codes: { code: string; libelle: string }[] = [];
  for (const l of lectures) for (const m of l.marqueurs || []) if (!codes.some((c) => c.code === m.code)) codes.push({ code: m.code, libelle: m.libelle });
  for (const c of codes) {
    ajouter(`m-${c.code}`, c.libelle, (l) => {
      const m = (l.marqueurs || []).find((x) => x.code === c.code);
      return m ? `${nb(m.valeur)} ${m.unite} · ${nb(m.mom)} MoM` : '—';
    });
  }
  ajouter('cn', 'Clarté nucale', (l) => (l.cn ? `${nb(l.cn.mm)} mm (LCC ${nb(l.cn.lccMm)} mm) · ${nb(l.cn.mom)} MoM` : 'non mesurée'));
  ajouter('corrections', 'Corrigées par', (l) => l.correctionsLigne || 'ligne absente');
  ajouter('signes', 'Signes échographiques', (l) => ((l.signes || []).length ? (l.signes || []).map((s) => `${s.libelle} ${s.resultat}`).join(', ') : 'aucun imprimé'));
  ajouter('t21', 'Risque T21', (l) => `${risqueLisible(l.risqueT21)}${l.libelleT21 ? ` — ${l.libelleT21}` : ''}`);
  ajouter('age', 'Risque lié à l’âge', (l) => risqueLisible(l.risqueAge));
  ajouter('t1318', 'Risque T13/18', (l) => `${risqueLisible(l.risqueT1318)}${l.libelleT1318 ? ` — ${l.libelleT1318}` : ''}`);
  if (lectures.some((l) => l.profil && !/^1er/i.test(l.profil))) ajouter('tn', 'Tube neural', (l) => l.tubeNeural || 'ligne absente');
  ajouter('edition', 'Édité par SsdwLab le', (l) => fr(l.dateEdition));
  return lignes;
}

/** En une phrase : ce qui a changé entre deux lectures (l'ancienne, la nouvelle). */
export function resumeChangements(lignes: LigneComparaison[]): string {
  const ch = lignes.filter((l) => l.change && l.cle !== 'edition');
  return ch.length ? ch.map((l) => l.libelle).join(', ') : 'aucune valeur lue ne change';
}
